import { useMutation, useQuery } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import { queryClient } from '../queryClient';
import { createIdempotencyKey } from '../utils/idempotencyKey';

export type EventRequestStatus =
  | 'pending'
  | 'approved'
  | 'declined'
  | 'needs_info'
  | 'completed'
  | 'contact_requestor'
  | 'on_hold';

export interface EventRequestTicketTypeDraft {
  name: string;
  description?: string | null;
  price_cents: number;
  currency?: string | null;
  sort_order?: number;
}

export interface EventRequest {
  id: string;
  sheet_row_id: string;
  title: string;
  description?: string | null;
  ministry?: string | null;
  event_type?: string | null;
  location?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  sheet_status?: string | null;
  sheet_approval?: string | null;
  submitted_at?: string | null;
  share_date?: string | null;
  status: EventRequestStatus;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
  image_url?: string | null;
  event_id?: string | null;
  is_ticketed?: boolean | null;
  ticket_types?: EventRequestTicketTypeDraft[] | null;
}

export interface CreateEventRequestInput {
  title: string;
  description?: string | null;
  ministry?: string | null;
  event_type?: string | null;
  location?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  share_date?: string | null;
  image_url?: string | null;
  requester_id?: string | null;
  is_ticketed?: boolean | null;
  ticket_types?: EventRequestTicketTypeDraft[] | null;
}

export interface UpdateMyEventRequestInput {
  id: string;
  requesterId?: string | null;
  contactEmail?: string | null;
  title?: string;
  description?: string | null;
  ministry?: string | null;
  event_type?: string | null;
  location?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  share_date?: string | null;
  image_url?: string | null;
  contact_name?: string | null;
  is_ticketed?: boolean | null;
  ticket_types?: EventRequestTicketTypeDraft[] | null;
}

type UpdateRequestPayload = {
  id: string;
  status?: EventRequestStatus;
  notes?: string;
  createEvent?: boolean;
  eventStatus?: string;
  image_url?: string;
  title?: string;
  description?: string | null;
  ministry?: string | null;
  event_type?: string | null;
  location?: string | null;
  start_date?: string | null;
  start_time?: string | null;
  share_date?: string | null;
  is_ticketed?: boolean | null;
  ticket_types?: EventRequestTicketTypeDraft[] | null;
};

type UpdateEventRequestResponse = {
  id?: string;
  status?: EventRequestStatus;
  createdEventId?: string | null;
  code?: string;
  message?: string;
  error?: string;
};

const invokeUpdateEventRequest = async (
  payload: UpdateRequestPayload,
  idempotencyKey: string,
) => {
  const { data, error } = await supabase.functions.invoke('update-event-request', {
    body: {
      ...payload,
      idempotencyKey,
    },
    headers: {
      'X-Idempotency-Key': idempotencyKey,
    },
  });

  if (error instanceof FunctionsHttpError && error.context?.json) {
    const errorBody = await error.context.json().catch(() => null) as UpdateEventRequestResponse | null;
    return {
      data: errorBody,
      error,
    };
  }

  return {
    data: data as UpdateEventRequestResponse | null,
    error,
  };
};

export const useEventRequests = () =>
  useQuery<EventRequest[]>({
    queryKey: ['event-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EventRequest[];
    },
  });

const buildSheetRowId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `web-${crypto.randomUUID()}`;
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const useCreateEventRequest = () =>
  useMutation({
    mutationFn: async (payload: CreateEventRequestInput) => {
      const { data: authData } = await supabase.auth.getUser();
      const requesterId = payload.requester_id ?? authData?.user?.id ?? null;
      if (!requesterId) {
        throw new Error('Please sign in again and retry.');
      }
      const sheet_row_id = buildSheetRowId();
      const submitted_at = new Date().toISOString();
      const normalizedContactEmail =
        typeof payload.contact_email === 'string' ? payload.contact_email.trim().toLowerCase() : null;

      const { data, error } = await supabase
        .from('event_requests')
        .insert({
          sheet_row_id,
          status: 'pending' as EventRequestStatus,
          sheet_status: 'pending',
          sheet_approval: 'Pending',
          submitted_at,
          requester_id: requesterId,
          ...payload,
          contact_email: normalizedContactEmail,
        })
        .select('*')
        .single();

      if (error) throw error;
      try {
        await notifyAdminsOfEventRequest(data as EventRequest);
      } catch (notifyError) {
        console.warn('[EventRequests] notify admins failed', notifyError);
      }
      return data as EventRequest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-requests'] });
    },
  });

const notifyAdminsOfEventRequest = async (request: EventRequest) => {
  const adminRoles = ['admin', 'super_admin', 'editor'];
  const titleText = request.title?.trim() || 'Untitled event';
  const senderName = request.contact_name?.trim() || request.contact_email || 'Someone';
  const message = `${senderName} submitted "${titleText}" for approval.`;

  const { data: authData } = await supabase.auth.getUser();
  const senderId = authData?.user?.id ?? null;

  const { data: admins, error: adminsError } = await supabase
    .from('users')
    .select('id')
    .overlaps('roles', adminRoles);

  if (!adminsError && admins && admins.length > 0) {
    const notificationRows = admins
      .map((admin) => admin.id)
      .filter(Boolean)
      .map((recipientId) => ({
        recipient_id: recipientId,
        sender_id: senderId,
        type: 'event_request',
        title: 'New event submission',
        message,
        data: {
          requestId: request.id,
          eventType: request.event_type,
          ministry: request.ministry,
          contactName: request.contact_name,
          contactEmail: request.contact_email,
        },
      }));

    if (notificationRows.length > 0) {
      const { error: feedError } = await supabase.from('notification_feed').insert(notificationRows);
      if (feedError) {
        console.warn('[EventRequests] notification_feed insert failed', feedError);
      }
    }
  } else if (adminsError) {
    console.warn('[EventRequests] admin lookup failed', adminsError);
  }

  await supabase.functions.invoke('send-push-notification-enhanced', {
    body: {
      title: 'New event submission',
      body: message,
      targetRoles: adminRoles,
      category: 'events',
      data: {
        requestId: request.id,
        eventType: request.event_type,
        ministry: request.ministry,
      },
    },
  });
};

export const useSyncEventRequests = () =>
  useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('sync-event-requests', { body: {} });
      if (error) {
        console.error('[SyncEventRequests] invoke error', error);
        throw error;
      }
      if (data && (data as { error?: string }).error) {
        const message = (data as { error: string; errorId?: string }).error;
        console.error('[SyncEventRequests] function error payload', data);
        throw new Error(message);
      }
      console.log('[SyncEventRequests] function success payload', data);
      return data as { upserted?: number; reason?: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-requests'] });
      queryClient.invalidateQueries({ queryKey: ['sync-meta'] });
    },
  });

export const useMyEventRequests = (params?: { requesterId?: string | null; contactEmail?: string | null }) =>
  useQuery<EventRequest[]>({
    queryKey: ['event-requests', 'mine', params?.requesterId ?? null, params?.contactEmail ?? null],
    enabled: Boolean(params?.requesterId || params?.contactEmail),
    queryFn: async () => {
      const requesterId = params?.requesterId?.trim() || null;
      const contactEmail = params?.contactEmail?.trim().toLowerCase() || null;
      if (!requesterId && !contactEmail) return [];

      let byRequesterId: EventRequest[] = [];
      if (requesterId) {
        const { data, error } = await supabase
          .from('event_requests')
          .select('*')
          .eq('requester_id', requesterId)
          .order('submitted_at', { ascending: false });
        if (error && !String(error.message || '').toLowerCase().includes('requester_id')) throw error;
        byRequesterId = (data ?? []) as EventRequest[];
      }

      let byEmail: EventRequest[] = [];
      if (contactEmail) {
        const { data, error } = await supabase
          .from('event_requests')
          .select('*')
          .ilike('contact_email', contactEmail)
          .order('submitted_at', { ascending: false });
        if (error) throw error;
        byEmail = (data ?? []) as EventRequest[];
      }

      const merged = new Map<string, EventRequest>();
      byRequesterId.forEach((row) => merged.set(row.id, row));
      byEmail.forEach((row) => merged.set(row.id, row));

      return Array.from(merged.values()).sort((a, b) => {
        const aTs = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
        const bTs = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
        return bTs - aTs;
      });
    },
  });

export const useUpdateMyEventRequest = () =>
  useMutation({
    mutationFn: async ({ id, requesterId, contactEmail, ...payload }: UpdateMyEventRequestInput) => {
      const { data: existing, error: fetchError } = await supabase
        .from('event_requests')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!existing) throw new Error('Submission not found.');

      const normalizedExistingEmail =
        typeof existing.contact_email === 'string' ? existing.contact_email.trim().toLowerCase() : '';
      const normalizedContactEmail = contactEmail?.trim().toLowerCase() || '';
      const rowRequesterId = typeof (existing as { requester_id?: string }).requester_id === 'string'
        ? (existing as { requester_id?: string }).requester_id
        : '';
      const ownsByRequester = Boolean(requesterId && rowRequesterId && requesterId === rowRequesterId);
      const ownsByEmail = Boolean(normalizedExistingEmail && normalizedContactEmail && normalizedExistingEmail === normalizedContactEmail);
      if (!ownsByRequester && !ownsByEmail) {
        throw new Error('You can only edit your own submissions.');
      }

      const updateBody: Record<string, unknown> = {
        ...payload,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('event_requests')
        .update(updateBody)
        .eq('id', id)
        .select('*')
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Could not update submission.');
      return data as EventRequest;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['event-requests'] });
      queryClient.invalidateQueries({
        queryKey: ['event-requests', 'mine', variables.requesterId ?? null, variables.contactEmail ?? null],
      });
    },
  });

export const useUpdateEventRequest = () =>
  useMutation({
    mutationFn: async (payload: UpdateRequestPayload) => {
      let idempotencyKey = createIdempotencyKey('web-event-request-update', payload);
      let { data, error } = await invokeUpdateEventRequest(payload, idempotencyKey);

      if (data?.code === 'idempotency_key_conflict') {
        idempotencyKey = `${idempotencyKey}:${Date.now().toString(36)}`;
        ({ data, error } = await invokeUpdateEventRequest(payload, idempotencyKey));
      }

      if (error && data?.message) {
        throw new Error(data.message);
      }
      if (error) throw error;
      if (data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      return data as { id: string; status: EventRequestStatus; createdEventId?: string | null };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-requests'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
    },
  });

